import { useEffect, useState } from "react";
import type { FileDetail as FileDetailData, MailboxInfo } from "../api";
import { fetchRawFile, mailboxLabel } from "../api";
import { saveBlob } from "../download";
import { formatSize, formatTimestamp } from "../format";
import { Overview } from "./Overview";
import { RecordConsole } from "./RecordConsole";
import { JsonView } from "./JsonView";
import { FileNameKey } from "./FileNameKey";

// One file, in the four readings the API returns. The views are ordered the way
// a question narrows: what is this delivery → what does each line say → the
// parsed structure → what the service published because of it.

type View = "overview" | "records" | "parsed" | "events";

const VIEWS: Array<{ id: View; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "records", label: "Records" },
  { id: "parsed", label: "Parsed" },
  { id: "events", label: "Event" },
];

export function FileDetail({
  file,
  token,
  mailboxes,
}: {
  file: FileDetailData;
  token: string;
  mailboxes: MailboxInfo[];
}): JSX.Element {
  const mailbox = mailboxes.find((info) => info.id === file.mailbox);
  const [view, setView] = useState<View>("overview");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();
  const hasRecords = file.decoded.lines.some((line) => line.fields.length > 0);

  // A receipt or a report has no records to overview — open it on the reading
  // that actually has something in it.
  useEffect(() => {
    setView(hasRecords ? "overview" : "records");
    setSaveError(undefined);
  }, [file.name, hasRecords]);

  // The download is a fetch, not a link, because the request has to carry the
  // bearer token — see saveBlob.
  async function save(): Promise<void> {
    setSaving(true);
    setSaveError(undefined);
    try {
      saveBlob(await fetchRawFile(token, file.mailbox, file.direction, file.name), file.name);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "That file could not be downloaded.");
    } finally {
      setSaving(false);
    }
  }

  const disabled: Record<View, boolean> = {
    overview: !hasRecords,
    records: false,
    parsed: file.parsed === null || file.parsed === undefined,
    events: (file.events ?? []).length === 0,
  };

  return (
    <section className="detail">
      <div className="slip">
        <div className="slip-head">
          <FileNameKey name={file.name} naming={file.naming} />
          <span className="route">
            {file.direction === "inbound" ? (
              <>
                <b>Bank</b> → us
              </>
            ) : (
              <>
                us → <b>Bank</b>
              </>
            )}
          </span>
        </div>

        <div className="slip-facts">
          <div className="fact">
            <span className="label">Content</span>
            <span className="value">{file.label ?? file.contentKind}</span>
          </div>
          <div className="fact">
            <span className="label">Delivery type</span>
            <span className="value">{file.deliveryType ?? "not a delivery"}</span>
          </div>
          <div className="fact">
            <span className="label">Mailbox</span>
            <span className="value">{mailbox ? mailboxLabel(mailbox) : file.mailbox}</span>
          </div>
          <div className="fact">
            <span className="label">{file.direction === "inbound" ? "Received" : "Written"}</span>
            <span className="value">{formatTimestamp(file.lastModified)}</span>
          </div>
          <div className="fact">
            <span className="label">Size</span>
            <span className="value">{formatSize(file.size)}</span>
          </div>
          <div className="fact">
            <span className="label">Lines</span>
            <span className="value">{file.decoded.lines.length}</span>
          </div>
          <div className="fact">
            <span className="label">Original</span>
            <span className="value">
              <button type="button" className="linkish" onClick={() => void save()} disabled={saving}>
                {saving ? "Fetching…" : "Download"}
              </button>
            </span>
          </div>
        </div>

        {(file.notes ?? []).length > 0 || saveError ? (
          <ul className="notes">
            {saveError ? <li data-tone="warn">{saveError}</li> : null}
            {(file.notes ?? []).map((note) => (
              <li key={note} data-tone={/refuses|could not|failed/i.test(note) ? "warn" : undefined}>
                {note}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="views" role="tablist">
        {VIEWS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={view === entry.id}
            disabled={disabled[entry.id]}
            onClick={() => setView(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="view">
        {view === "overview" ? <Overview lines={file.decoded.lines} /> : null}
        {view === "records" ? (
          <RecordConsole lines={file.decoded.lines} width={file.decoded.recordWidth} />
        ) : null}
        {view === "parsed" ? <JsonView value={file.parsed} /> : null}
        {view === "events" ? <JsonView value={file.events} /> : null}
      </div>
    </section>
  );
}
